import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureMySqlLinkedService } from 'app/model/linkedservice/azure-db-mysql.linkedservice';
import { Constants } from 'app/shared/shared-constants';
import { akvToResource, akvToModel, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { SSLMode, convertToSSLMode, convertToUseSystemTrustStore } from 'app/registries/consts/mysql-consts';
import { connectionStringPartToResource, connectionStringPartToModel } from 'app/shared/utils/linkedservice-parameter.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, ITridentResource } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureMySqlLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: AzureMySqlLinkedService) => {
                    // if password is in AKV, then connectionString won't contain secret
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV && ls.password instanceof AzureKeyVault) {
                        return '*';
                    }
                    return 'secret';
                },
                password: 'secret',
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureMySqlLinkedServiceAdapter extends LinkedServiceAdapter<AzureMySqlLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => AzureMySqlLinkedService {
        return AzureMySqlLinkedService;
    }

    protected model(): IModel {
        return AzureMySqlLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureMySqlLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureMySqlLinkedService;
        properties.type = Metamodel.modelFor(AzureMySqlLinkedService).name;

        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Server', connectionStringPartToResource(linkedService, AzureMySqlLinkedService.SERVER_NAME));
            builder.add('Port', connectionStringPartToResource(linkedService, AzureMySqlLinkedService.PORT));
            builder.add('Database', connectionStringPartToResource(linkedService, AzureMySqlLinkedService.DATABASE_NAME));
            builder.add('UID', connectionStringPartToResource(linkedService, AzureMySqlLinkedService.USER_NAME));
            if (!(linkedService.password instanceof AzureKeyVault)) {
                const password = connectionStringPartToResource(linkedService, AzureMySqlLinkedService.PASSWORD);
                if (password) {
                    builder.add('PWD', password);
                }
            }

            builder.add('SSLMode', linkedService.sslMode != null ? linkedService.sslMode.toString() : '1');
            if (linkedService.sslMode !== SSLMode.Disabled) {
                builder.add('UseSystemTrustStore', linkedService.useSystemTrustStore ? '1' : '0');
            }
            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.serverName === Constants.maskedCredential &&
                linkedService.port === undefined &&
                linkedService.databaseName === Constants.maskedCredential &&
                linkedService.userName === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }

        properties.typeProperties = {
            connectionString,
            password: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureMySqlLinkedService): Promise<AzureMySqlLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureMySqlLinkedService;

        // Cleanup, in case non of the if-else statement got executed
        AzureMySqlLinkedService.SERVER_NAME.setValue(linkedService, undefined);
        AzureMySqlLinkedService.SERVER_NAME.setTemplateValue(linkedService, undefined);
        AzureMySqlLinkedService.DATABASE_NAME.setValue(linkedService, undefined);
        AzureMySqlLinkedService.DATABASE_NAME.setTemplateValue(linkedService, undefined);
        AzureMySqlLinkedService.PORT.setValue(linkedService, undefined);
        AzureMySqlLinkedService.PORT.setTemplateValue(linkedService, undefined);
        AzureMySqlLinkedService.USER_NAME.setValue(linkedService, undefined);
        AzureMySqlLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
        AzureMySqlLinkedService.PASSWORD.setValue(linkedService, undefined);
        AzureMySqlLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
        linkedService.sslMode = SSLMode.Preferred;
        linkedService.useSystemTrustStore = false;

        if (isString(properties.typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.parse(properties.typeProperties.connectionString as string);

            connectionStringPartToModel(linkedService, AzureMySqlLinkedService.SERVER_NAME, builder.getValue('Server', false) || '');
            connectionStringPartToModel(linkedService, AzureMySqlLinkedService.DATABASE_NAME, builder.getValue('Database', false) || '');
            connectionStringPartToModel(linkedService, AzureMySqlLinkedService.PORT, builder.getValue('Port', false));
            connectionStringPartToModel(linkedService, AzureMySqlLinkedService.USER_NAME, builder.getValue('UID', false) || builder.getValue('User', false) || '');
            linkedService.sslMode = convertToSSLMode(builder.getValue('SSLMode', false));
            linkedService.useSystemTrustStore = linkedService.sslMode !== SSLMode.Disabled ?
                convertToUseSystemTrustStore(builder.getValue('UseSystemTrustStore', false)) : false;
            connectionStringPartToModel(linkedService, AzureMySqlLinkedService.PASSWORD, builder.getValue('PWD', false) || builder.getValue('Password', false) || '', true);
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'Database', 'UID', 'User', 'SSLMode', 'UseSystemTrustStore', 'PWD', 'Password']);
            if (properties.typeProperties.password) {
                parameterizableSecretBaseToModel(linkedService, AzureMySqlLinkedService.PASSWORD, properties.typeProperties.password, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.serverName = Constants.maskedCredential;
            linkedService.databaseName = Constants.maskedCredential;
            linkedService.userName = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            linkedService.port = undefined;
            if (properties.typeProperties.password) {
                parameterizableSecretBaseToModel(linkedService, AzureMySqlLinkedService.PASSWORD, properties.typeProperties.password, this.context);
            }
        } else if (properties.typeProperties.connectionString
            && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
            linkedService.enableAKV = true;
        }

        return linkedService;
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.AzureMySqlLinkedServiceTypeProperties> => {
        const builder = new DbConnectionStringBuilderService(true);
        const connectionDetails = JSON.parse(tridentResource.connectionDetails);
        const extensionDataSourcePath = connectionDetails.extensionDataSourcePath;
        const [serverName='',  databaseName=''] = extensionDataSourcePath.split(';');
        builder.add('Server', serverName);
        builder.add('Database', databaseName);
        return { connectionString: this._connectionStringToResource(builder.connectionString) };
    }
}
